import React, { useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import PortfolioNav from "../components/PortfolioNav";
import { ArrowRight } from "lucide-react";

// Import project data and helpers
import { 
  PROJECTS, 
  PROJECT_TYPES, 
  getProjectBySlug, 
  getNextProject,
  getAdditionalGrids 
} from "../data/projects";

// Lazy load gallery components
const IMTMGallery = React.lazy(() => import("../components/IMTMGallery"));
const DejaBlueGallery = React.lazy(() => import("../components/DejaBlueGallery"));
const BobGallery = React.lazy(() => import("../components/BobGallery"));
const PolygonGallery = React.lazy(() => import("../components/PolygonGallery"));
const OKDWebsiteMockup = React.lazy(() => import("../components/OKDWebsiteMockup"));
const WorxGallery = React.lazy(() => import("../components/WorxGallery"));
const BalsanGallery = React.lazy(() => import("../components/BalsanGallery"));
const CeremonieGallery = React.lazy(() => import("../components/CeremonieGallery"));
const SmooveeGallery = React.lazy(() => import("../components/SmooveeGallery"));

// Lazy load templates
const SocialMediaTemplate = React.lazy(() => import("../components/templates/SocialMediaTemplate"));
const CampaignTemplate = React.lazy(() => import("../components/templates/CampaignTemplate"));
const BrandingTemplate = React.lazy(() => import("../components/templates/BrandingTemplate"));
const PrintTemplate = React.lazy(() => import("../components/templates/PrintTemplate"));
const MomentsTemplate = React.lazy(() => import("../components/templates/MomentsTemplate"));
const MonoxTemplate = React.lazy(() => import("../components/templates/MonoxTemplate"));
const TalksTemplate = React.lazy(() => import("../components/templates/TalksTemplate"));
const GameTemplate = React.lazy(() => import("../components/templates/GameTemplate"));

// Gallery component map
const GALLERY_COMPONENTS = {
  IMTMGallery,
  DejaBlueGallery,
  BobGallery,
  PolygonGallery,
  WorxGallery,
  BalsanGallery,
  CeremonieGallery,
  SmooveeGallery,
};

// ═══════════════════════════════════════════════════════════════════════════════
// ANIMATED IMAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function AnimatedImage({ src, alt, index, isWide }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ 
        duration: 0.7, 
        delay: index * 0.08,
        ease: [0.25, 0.46, 0.45, 0.94] 
      }}
      style={{
        gridColumn: isWide ? "1 / -1" : "auto",
        overflow: "hidden",
        borderRadius: "8px",
      }}
    >
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
        whileHover={{ scale: 1.03 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      />
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE TRANSITION
// ═══════════════════════════════════════════════════════════════════════════════
const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  },
  exit: { 
    opacity: 0,
    transition: { duration: 0.3 }
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// NEXT PROJECT SECTION (shared across all templates)
// ═══════════════════════════════════════════════════════════════════════════════
function NextProjectSection({ nextProject }) {
  return (
    <Link
      to={`/project/${nextProject.slug}`}
      style={{ textDecoration: "none" }}
    >
      <motion.div
        whileHover={{ backgroundColor: "#FAFAFA" }}
        style={{
          padding: "24px 24px",
          textAlign: "center",
          cursor: "pointer",
          borderTop: "1px solid #EEEEEE",
        }}
      >
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: "0.6rem",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "#AAAAAA",
            marginBottom: "8px",
          }}
        >
          Next Project
        </p>
        
        <motion.h2
          whileHover={{ scale: 1.02 }}
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(1.2rem, 3vw, 1.8rem)",
            letterSpacing: "-0.02em",
            textTransform: "uppercase",
            background: "#8B8BF5",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            margin: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {nextProject.title}
          <ArrowRight size={18} style={{ color: "#8B8BF5" }} />
        </motion.h2>
      </motion.div>
    </Link>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DEFAULT TEMPLATE (for campaigns, generic projects)
// ═══════════════════════════════════════════════════════════════════════════════
function DefaultTemplate({ project }) {
  return (
    <>
      {/* Header */}
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "100px 24px 32px",
        }}
      >
        {/* Role & Client */}
        {project.role && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "32px",
              marginBottom: "32px",
            }}
          >
            <div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#AAAAAA", display: "block", marginBottom: "6px" }}>Role</span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "0.9rem", color: "#333333" }}>{project.role}</span>
            </div>
            {project.client && (
              <div>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#AAAAAA", display: "block", marginBottom: "6px" }}>Client</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "0.9rem", color: "#333333" }}>{project.client}</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Subtitle & Year (for non-role projects) */}
        {!project.role && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}
          >
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.75rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#999999" }}>{project.subtitle}</span>
            <span style={{ color: "#DDDDDD" }}>-</span>
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "0.75rem", color: "#999999" }}>{project.year}</span>
          </motion.div>
        )}

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 900,
            fontSize: "clamp(3rem, 10vw, 7rem)",
            lineHeight: 0.95,
            letterSpacing: "-0.04em",
            textTransform: "uppercase",
            background: "#8B8BF5",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            margin: "0 0 24px 0",
          }}
        >
          {project.title}
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300,
            fontSize: "clamp(1.1rem, 2vw, 1.35rem)",
            lineHeight: 1.7,
            color: "#444444",
            maxWidth: "700px",
          }}
        >
          {project.description}
        </motion.p>
      </div>

      {/* First Image */}
      {project.images && project.images[0] && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          style={{ width: "100%", padding: "0 24px", marginBottom: "80px" }}
        >
          <img
            src={project.images[0]}
            alt={project.title}
            style={{ width: "100%", height: "auto", maxHeight: "70vh", objectFit: "cover", borderRadius: "8px" }}
          />
        </motion.div>
      )}

      {/* Challenge & Solution */}
      {project.challenge && (
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "0 24px 80px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "32px",
          }}
        >
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8B8BF5", marginBottom: "16px" }}>The Challenge</h3>
            <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "0.9rem", lineHeight: 1.75, color: "#555555" }}>{project.challenge}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
            <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8B8BF5", marginBottom: "16px" }}>The Solution</h3>
            <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "0.9rem", lineHeight: 1.75, color: "#555555" }}>{project.solution}</p>
          </motion.div>
        </div>
      )}

      {/* Image Gallery */}
      {project.images && project.images.length > 1 && (
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px 100px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
            {project.images.slice(1).map((img, index) => (
              <AnimatedImage key={index} src={img} alt={`${project.title} ${index + 2}`} index={index} isWide={index === 1} />
            ))}
          </div>
        </div>
      )}

      {/* Second Campaign */}
      {project.secondCampaign && (
        <>
          <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px 60px" }}>
            <motion.div initial={{ opacity: 0, scaleX: 0 }} whileInView={{ opacity: 1, scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} style={{ height: "1px", background: "#8B8BF5" }} />
          </div>
          <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px 60px" }}>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 style={{ fontFamily: "'Garet', sans-serif", fontWeight: 700, fontSize: "clamp(1.8rem, 5vw, 3rem)", letterSpacing: "-0.01em", color: "#8B8BF5", marginBottom: "16px" }}>{project.secondCampaign.title}</h2>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "1.1rem", lineHeight: 1.7, color: "#555555", maxWidth: "600px" }}>{project.secondCampaign.description}</p>
            </motion.div>
          </div>
          <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px 100px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
              {project.secondCampaign.images.map((img, index) => (
                <AnimatedImage key={`second-${index}`} src={img} alt={`${project.secondCampaign.title} ${index + 1}`} index={index} isWide={index === 0 || index === 2} />
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEGACY BRANDING TEMPLATE (Bob, Polygon - uses gallery components)
// ═══════════════════════════════════════════════════════════════════════════════
function LegacyBrandingTemplate({ project }) {
  const GalleryComponent = GALLERY_COMPONENTS[project.galleryComponent];
  
  return (
    <>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "100px 24px 32px" }}>
        {project.role && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }} style={{ display: "flex", flexWrap: "wrap", gap: "32px", marginBottom: "32px" }}>
            <div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#AAAAAA", display: "block", marginBottom: "6px" }}>Role</span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "0.9rem", color: "#333333" }}>{project.role}</span>
            </div>
            {project.client && (
              <div>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#AAAAAA", display: "block", marginBottom: "6px" }}>Client</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "0.9rem", color: "#333333" }}>{project.client}</span>
              </div>
            )}
          </motion.div>
        )}
        
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }} style={{ fontFamily: "'Garet', sans-serif", fontWeight: 700, fontSize: "clamp(3rem, 10vw, 7rem)", lineHeight: 0.95, letterSpacing: "-0.01em", textTransform: "uppercase", color: "#8B8BF5", margin: "0 0 24px 0" }}>{project.title}</motion.h1>
      </div>

      {/* Gallery */}
      {GalleryComponent && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }} style={{ width: "100%", marginBottom: "40px" }}>
          <React.Suspense fallback={<div style={{ width: "100%", maxWidth: "900px", margin: "0 auto", height: "500px", display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontFamily: "'Inter', sans-serif", background: "#f5f5f5", borderRadius: "12px" }}>Loading gallery...</div>}>
            <GalleryComponent introText={project.introText} />
          </React.Suspense>
        </motion.div>
      )}

      {/* Description - Left aligned */}
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px 24px" }}>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "clamp(0.875rem, 1.2vw, 0.95rem)", lineHeight: 1.8, color: "#444444", textAlign: "left", marginBottom: "24px", maxWidth: "700px" }}>{project.description}</motion.p>
        {project.description2 && (
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "clamp(0.875rem, 1.2vw, 0.95rem)", lineHeight: 1.8, color: "#444444", textAlign: "left", maxWidth: "700px" }}>{project.description2}</motion.p>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHOWCASE TEMPLATE (OKD, Bloom)
// ═══════════════════════════════════════════════════════════════════════════════
function ShowcaseTemplate({ project }) {
  const isOKD = project.slug === "okd";
  
  return (
    <>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "100px 24px 32px" }}>
        {project.role && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }} style={{ display: "flex", flexWrap: "wrap", gap: "32px", marginBottom: "32px" }}>
            <div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#AAAAAA", display: "block", marginBottom: "6px" }}>Role</span>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "0.9rem", color: "#333333" }}>{project.role}</span>
            </div>
            {project.client && (
              <div>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#AAAAAA", display: "block", marginBottom: "6px" }}>Client</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: "0.9rem", color: "#333333" }}>{project.client}</span>
              </div>
            )}
          </motion.div>
        )}
        
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }} style={{ fontFamily: "'Garet', sans-serif", fontWeight: 700, fontSize: "clamp(3rem, 10vw, 7rem)", lineHeight: 0.95, letterSpacing: "-0.01em", textTransform: "uppercase", color: "#8B8BF5", margin: "0 0 24px 0" }}>{project.title}</motion.h1>
      </div>

      {/* OKD Mockup */}
      {isOKD && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }} style={{ width: "100%", marginBottom: "40px" }}>
          <React.Suspense fallback={<div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto", height: "650px", display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontFamily: "'Inter', sans-serif", background: "#f5f5f5", borderRadius: "12px" }}>Loading website mockup...</div>}>
            <OKDWebsiteMockup />
          </React.Suspense>
        </motion.div>
      )}

      {/* Description - Left aligned */}
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px 24px" }}>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "clamp(0.875rem, 1.2vw, 0.95rem)", lineHeight: 1.8, color: "#444444", textAlign: "left", marginBottom: "24px", maxWidth: "700px" }}>{project.description}</motion.p>
        {project.description2 && (
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "clamp(0.875rem, 1.2vw, 0.95rem)", lineHeight: 1.8, color: "#444444", textAlign: "left", maxWidth: "700px" }}>{project.description2}</motion.p>
        )}
      </div>

      {/* Images for non-OKD showcase projects */}
      {!isOKD && project.images && project.images[0] && (
        <>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }} style={{ width: "100%", padding: "0 24px", marginBottom: "80px" }}>
            <img src={project.images[0]} alt={project.title} style={{ width: "100%", height: "auto", maxHeight: "70vh", objectFit: "cover", borderRadius: "8px" }} />
          </motion.div>
          
          {project.challenge && (
            <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px 80px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "32px" }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8B8BF5", marginBottom: "16px" }}>The Challenge</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "0.9rem", lineHeight: 1.75, color: "#555555" }}>{project.challenge}</p>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.7rem", letterSpacing: "0.15em", textTransform: "uppercase", color: "#8B8BF5", marginBottom: "16px" }}>The Solution</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: "0.9rem", lineHeight: 1.75, color: "#555555" }}>{project.solution}</p>
              </motion.div>
            </div>
          )}

          {project.images.length > 1 && (
            <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px 100px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
                {project.images.slice(1).map((img, index) => (
                  <AnimatedImage key={index} src={img} alt={`${project.title} ${index + 2}`} index={index} isWide={index === 1} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function Project() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug) || PROJECTS[0];
  const nextProject = getNextProject(slug);
  const additionalGrids = getAdditionalGrids(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Get gallery component for social media projects
  const GalleryComponent = project.galleryComponent ? GALLERY_COMPONENTS[project.galleryComponent] : null;

  // Render content based on project type
  const renderContent = () => {
    switch (project.type) {
      case PROJECT_TYPES.SOCIAL_MEDIA:
        return (
          <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
            <SocialMediaTemplate 
              project={project} 
              GalleryComponent={GalleryComponent}
              additionalGrids={additionalGrids}
            />
          </React.Suspense>
        );
      
      case PROJECT_TYPES.BRANDING:
        // Use MonoxTemplate for Monox+ project
        if (project.slug === "monox") {
          return (
            <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
              <MonoxTemplate project={project} />
            </React.Suspense>
          );
        }
        // Use TalksTemplate for Talks project
        if (project.slug === "talks") {
          return (
            <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
              <TalksTemplate project={project} />
            </React.Suspense>
          );
        }
        // Use MomentsTemplate for Moments project
        if (project.slug === "moments") {
          return (
            <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
              <MomentsTemplate project={project} />
            </React.Suspense>
          );
        }
        // Use BrandingTemplate for projects with mainImages
        if (project.mainImages && project.mainImages.length > 0) {
          return (
            <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
              <BrandingTemplate project={project} />
            </React.Suspense>
          );
        }
        return <LegacyBrandingTemplate project={project} />;
      
      case PROJECT_TYPES.SHOWCASE:
        return <ShowcaseTemplate project={project} />;
      
      case PROJECT_TYPES.CREATIVE:
        if (project.mainImages && project.mainImages.length > 0) {
          return (
            <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
              <BrandingTemplate project={project} />
            </React.Suspense>
          );
        }
        return <LegacyBrandingTemplate project={project} />;
      
      case PROJECT_TYPES.CAMPAIGN:
        return (
          <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
            <CampaignTemplate 
              project={project} 
              images={project.images || []}
            />
          </React.Suspense>
        );
      
      case PROJECT_TYPES.PRINT:
        return (
          <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
            <PrintTemplate project={project} />
          </React.Suspense>
        );
      
      case PROJECT_TYPES.GAME:
        return (
          <React.Suspense fallback={<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>Loading...</div>}>
            <GameTemplate project={project} />
          </React.Suspense>
        );
      
      case PROJECT_TYPES.PRODUCT:
      default:
        return <DefaultTemplate project={project} />;
    }
  };

  return (
    <motion.div
      data-testid="project-page"
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{
        minHeight: "100vh",
        background: "#FFFFFF",
      }}
    >
      {renderContent()}

      {/* Next Project - Hidden for projects with their own layout */}
      {project.slug !== "moments" && project.slug !== "valhalla" && (
        <NextProjectSection nextProject={nextProject} />
      )}
    </motion.div>
  );
}
